<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Input from '../../Input.svelte';
  import Button from '../../Button.svelte';
  import Notification from '../../Notification.svelte';
  import { MyEmployerService, type InviteEmployerRequest } from '../../../lib/api/employee/myEmployer.service';
  import { AuthService } from '../../../lib/api/auth.service';

  const dispatch = createEventDispatcher();

  export let isOpen = false;
  export let existingEmployers: Array<{ uuid: string; name: string }> = []; // Employers user is already linked to

  // Form data
  let name = '';
  let contact_email = '';
  let contact_phone = '';

  // Loading and notification states
  let loading = false;
  let showNotification = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  // Search existing employers
  let searchingExistingEmployers = false;
  let existingEmployerSearchResults: Array<{ uuid: string; name: string; isLinked: boolean }> = [];
  let existingEmployerSearchTimeout: ReturnType<typeof setTimeout> | undefined;

  function closeModal() {
    isOpen = false;
    // Reset form
    name = '';
    contact_email = '';
    contact_phone = '';
    showNotification = false;
    existingEmployerSearchResults = [];
    searchingExistingEmployers = false;
    if (existingEmployerSearchTimeout) {
      clearTimeout(existingEmployerSearchTimeout);
    }
    dispatch('close');
  }

  async function searchExistingEmployers() {
    if (existingEmployerSearchTimeout) {
      clearTimeout(existingEmployerSearchTimeout);
    }

    const searchQuery = name?.trim() || '';
    if (!searchQuery || searchQuery.length < 2) {
      existingEmployerSearchResults = [];
      searchingExistingEmployers = false;
      return;
    }

    existingEmployerSearchTimeout = setTimeout(async () => {
      const currentQuery = name?.trim() || '';
      if (!currentQuery || currentQuery.length < 2) {
        existingEmployerSearchResults = [];
        searchingExistingEmployers = false;
        return;
      }

      searchingExistingEmployers = true;
      
      try {
        const user = AuthService.getUser();
        if (!user?.uuid) {
          existingEmployerSearchResults = [];
          searchingExistingEmployers = false;
          return;
        }

        const result = await MyEmployerService.searchAllEmployers(user.uuid, currentQuery);
        
        if (result.success && result.data) {
          // Get UUIDs of employers user is already linked to
          const linkedUuids = new Set(existingEmployers.map(e => e.uuid));
          
          // Filter out already-linked employers and limit to 3 results
          // But also show linked ones with a badge (up to 3 total)
          const unlinked = result.data.filter(emp => !linkedUuids.has(emp.uuid));
          const linked = result.data.filter(emp => linkedUuids.has(emp.uuid));
          
          // Combine: show unlinked first (up to 3), then linked if space
          const unlinkedResults = unlinked.slice(0, 3).map(emp => ({ 
            uuid: emp.uuid, 
            name: emp.name,
            isLinked: false
          }));
          
          const linkedResults = linked.slice(0, Math.max(0, 3 - unlinkedResults.length)).map(emp => ({ 
            uuid: emp.uuid, 
            name: emp.name,
            isLinked: true
          }));
          
          existingEmployerSearchResults = [...unlinkedResults, ...linkedResults];
        } else {
          existingEmployerSearchResults = [];
        }
      } catch (err: any) {
        console.error('Error searching employers:', err);
        existingEmployerSearchResults = [];
      } finally {
        searchingExistingEmployers = false;
      }
    }, 300);
  }

  async function handleLinkEmployer(employerUuid: string, employerName: string) {
    loading = true;
    
    try {
      const user = AuthService.getUser();
      if (!user?.uuid) {
        showError('User not found');
        return;
      }

      const result = await MyEmployerService.linkToExistingEmployer(user.uuid, employerUuid);
      
      if (result.success) {
        showSuccess(result.message || 'Successfully linked to employer');
        setTimeout(() => {
          closeModal();
          dispatch('employer-linked');
        }, 2000);
      } else {
        showError(result.message || 'Failed to link employer');
      }
    } catch (err: any) {
      showError(err.message || 'An unexpected error occurred');
    } finally {
      loading = false;
    }
  }

  // Watch for name changes to trigger search
  $: searchTrigger = isOpen ? name : '';
  $: if (isOpen && searchTrigger) {
    const nameLength = searchTrigger.trim().length;
    if (nameLength >= 2) {
      searchExistingEmployers();
    } else {
      existingEmployerSearchResults = [];
      searchingExistingEmployers = false;
      if (existingEmployerSearchTimeout) {
        clearTimeout(existingEmployerSearchTimeout);
        existingEmployerSearchTimeout = undefined;
      }
    }
  }

  function showSuccess(message: string) {
    notificationType = 'success';
    notificationMessage = message;
    showNotification = true;
  }

  function showError(message: string) {
    notificationType = 'error';
    notificationMessage = message;
    showNotification = true;
  }

  function dismissNotification() {
    showNotification = false;
  }

  async function sendInvite() {
    if (!name.trim() || !contact_email.trim()) {
      showError('Employer name and contact email are required');
      return;
    }

    try {
      loading = true;
      showNotification = false;

      const requestData: InviteEmployerRequest = {
        name: name.trim(),
        contact_name: name.trim(), // Using name as contact_name since we don't have separate field
        contact_email: contact_email.trim(),
        contact_phone: contact_phone.trim() || undefined,
      };

      const result = await MyEmployerService.inviteEmployer(requestData);

      if (result.success) {
        showSuccess(result.message || 'Invitation sent successfully!');
        // Close modal after showing success notification and refresh the list
        setTimeout(() => {
          closeModal();
          dispatch('employer-linked');
        }, 2000);
      } else {
        // Handle API errors
        let errorMsg = result.message || 'Failed to send invitation';
        
        // If there are specific field errors, show them
        if (result.errors && typeof result.errors === 'object') {
          if (result.errors.errors) {
            // Laravel validation errors format
            const firstError = Object.values(result.errors.errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMsg = firstError[0];
            }
          }
        }
        
        showError(errorMsg);
      }
    } catch (err: any) {
      showError(err.message || 'An unexpected error occurred');
    } finally {
      loading = false;
    }
  }

  // Close modal on escape key
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isOpen) {
      closeModal();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
  <div 
    class="modal-overlay" 
    on:click|self={closeModal}
    on:keydown={(e) => e.key === 'Escape' && closeModal()}
    role="presentation"
  >
    <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="invite-employer-title">
      <div class="modal-content">
        <h2 id="invite-employer-title" class="modal-title">Invite Employer</h2>

        <div class="info-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>Your name and email will be included in the invitation so your employer knows who referred them.</span>
        </div>
        
        <div class="form-field">
          <Input
            label="Employer Name"
            placeholder="Enter employer name"
            bind:value={name}
            fullWidth={true}
            disabled={loading}
          />
          
          <!-- Show existing employers that can be linked -->
          {#if searchingExistingEmployers}
            <div class="loading-state-inline">
              <div class="loading-spinner-small"></div>
              <p>Searching for existing employers...</p>
            </div>
          {:else if existingEmployerSearchResults.length > 0}
            <div class="existing-employers-section">
              <p class="existing-employers-label">Found existing employers:</p>
              <div class="existing-employers-list">
                {#each existingEmployerSearchResults as employer}
                  {#if employer.isLinked}
                    <div class="existing-employer-item linked-employer-item" role="status">
                      <span class="employer-name-text">{employer.name}</span>
                      <span class="already-linked-badge">Already Linked</span>
                    </div>
                  {:else}
                    <button
                      type="button"
                      class="existing-employer-item"
                      on:click={() => handleLinkEmployer(employer.uuid, employer.name)}
                      disabled={loading}
                    >
                      <span class="employer-name-text">{employer.name}</span>
                      <svg class="link-icon-small" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      <span class="link-text">Link</span>
                    </button>
                  {/if}
                {/each}
              </div>
            </div>
          {:else if name.trim().length >= 2 && !searchingExistingEmployers}
            <p class="no-results-hint">No existing employers found. You can create a new one below.</p>
          {/if}
        </div>
        
        <div class="form-field">
          <Input
            label="Contact Email"
            type="email"
            placeholder="Enter contact email"
            bind:value={contact_email}
            fullWidth={true}
            disabled={loading}
          />
        </div>
        
        <div class="form-field">
          <Input
            label="Contact Phone Number"
            placeholder="Enter phone number (optional)"
            bind:value={contact_phone}
            fullWidth={true}
            disabled={loading}
          />
        </div>
        
        <div class="modal-actions">
          <Button 
            variant="primary" 
            fullWidth={true}
            on:click={sendInvite}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Invite'}
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Notification Component -->
<Notification 
  type={notificationType}
  message={notificationMessage}
  show={showNotification}
  onDismiss={dismissNotification}
/>

<style>
  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-container {
    background-color: white;
    width: 100%;
    max-width: 480px;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    border: 1px solid #E5E7EB;
  }

  .modal-content {
    padding: 2rem;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    text-align: center;
    margin: 0 0 1rem 0;
  }

  .info-note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    font-size: 0.8125rem;
    color: #1e40af;
    line-height: 1.4;
  }

  .info-note svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #3b82f6;
  }

  .form-field {
    margin-bottom: 1.5rem;
  }

  .modal-actions {
    margin-top: 2rem;
  }

  .modal-actions :global(button) {
    height: 56px;
    border-radius: 100px;
    font-weight: 600;
    font-size: 16px;
    color: black;
  }

  .modal-actions :global(button:hover) {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  }

  /* Existing employers search styles */
  .loading-state-inline {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .loading-spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top-color: var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .existing-employers-section {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f0f9ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
  }

  .existing-employers-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1e40af;
    margin: 0 0 0.75rem 0;
  }

  .existing-employers-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .existing-employer-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    width: 100%;
    gap: 0.75rem;
  }

  .existing-employer-item:hover:not(:disabled) {
    border-color: #3b82f6;
    background-color: #eff6ff;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  }

  .existing-employer-item:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .employer-name-text {
    flex: 1;
    font-weight: 500;
    color: #111827;
    text-align: left;
  }

  .link-icon-small {
    color: #3b82f6;
    flex-shrink: 0;
  }

  .link-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #3b82f6;
    flex-shrink: 0;
  }

  .linked-employer-item {
    cursor: default;
    opacity: 0.7;
  }

  .linked-employer-item:hover {
    border-color: #bfdbfe;
    background-color: white;
    box-shadow: none;
  }

  .already-linked-badge {
    font-size: 0.75rem;
    font-weight: 500;
    color: #059669;
    background-color: #d1fae5;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .no-results-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.5rem 0 0 0;
    font-style: italic;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .modal-container {
      width: 95%;
      margin: 0 10px;
    }
    
    .modal-content {
      padding: 1.5rem;
    }
  }
</style> 